import React from 'react';
import Box from '@mui/material/Box';
import {
  QuickFilter,
  QuickFilterTrigger,
  QuickFilterControl,
  QuickFilterClear,
  ToolbarButton,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-pro';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const SEARCH_FIELD_WIDTH = 200;

const searchFieldSx = {
  alignSelf: 'center',
  '& .MuiInput-root': { alignItems: 'center' },
  '& .MuiInput-underline:before': { borderBottomColor: 'divider' },
};

const quickFilterWrapperSx = {
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'center',
};

function QuickFilterClearButton() {
  return (
    <QuickFilterClear
      render={({ onClick, ...clearProps }) => (
        <InputAdornment position="end">
          <IconButton
            {...clearProps}
            size="small"
            onClick={onClick}
            aria-label="Clear search"
            edge="end"
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      )}
    />
  );
}

const QuickFilterSearchInput = React.forwardRef(function QuickFilterSearchInput(
  { value, sx, ...controlProps },
  ref,
) {
  return (
    <TextField
      {...controlProps}
      value={value}
      inputRef={ref}
      size="small"
      variant="standard"
      placeholder="Search…"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: 'action.active' }} />
            </InputAdornment>
          ),
          endAdornment: value ? <QuickFilterClearButton /> : null,
        },
      }}
      sx={{ width: SEARCH_FIELD_WIDTH, ...searchFieldSx, ...sx }}
    />
  );
});

export function CustomQuickFilter(props) {
  const { debounceMs = 150, ...rest } = props;

  return (
    <Box sx={quickFilterWrapperSx}>
      <QuickFilter defaultExpanded debounceMs={debounceMs} {...rest}>
        <QuickFilterControl
          render={({ ref, ...controlProps }) => (
            <QuickFilterSearchInput {...controlProps} ref={ref} />
          )}
        />
      </QuickFilter>
    </Box>
  );
}

export function CollapsibleQuickFilter(props) {
  const { debounceMs = 150, reserveExpandedWidth = false, ...rest } = props;
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const Tooltip = rootProps.slots.baseTooltip;
  const toolbarIconColor = (active) => (active ? 'primary' : 'default');

  return (
    <Box
      sx={{
        ...quickFilterWrapperSx,
        ...(reserveExpandedWidth
          ? { width: SEARCH_FIELD_WIDTH, minWidth: SEARCH_FIELD_WIDTH, flexShrink: 0 }
          : null),
      }}
    >
      <QuickFilter
        debounceMs={debounceMs}
        {...rest}
        render={(quickFilterProps, state) => (
          <Box
            {...quickFilterProps}
            sx={{
              display: 'grid',
              alignItems: 'center',
              alignSelf: 'center',
              width: reserveExpandedWidth ? SEARCH_FIELD_WIDTH : undefined,
              ...quickFilterProps.sx,
            }}
          >
            <QuickFilterTrigger
              render={(triggerProps, triggerState) => (
                <Tooltip
                  title={apiRef.current.getLocaleText('toolbarQuickFilterLabel')}
                  enterDelay={1000}
                  {...rootProps.slotProps?.baseTooltip}
                >
                  <ToolbarButton
                    {...triggerProps}
                    color={toolbarIconColor(triggerState.expanded || Boolean(triggerState.value))}
                    aria-disabled={triggerState.expanded}
                    aria-label={apiRef.current.getLocaleText('toolbarQuickFilterLabel')}
                    sx={{
                      gridArea: '1 / 1',
                      justifySelf: reserveExpandedWidth ? 'start' : undefined,
                      width: 'min-content',
                      height: 'min-content',
                      zIndex: 1,
                      opacity: triggerState.expanded ? 0 : 1,
                      pointerEvents: triggerState.expanded ? 'none' : 'auto',
                      transition: (theme) => theme.transitions.create('opacity'),
                    }}
                  >
                    <SearchIcon fontSize="small" />
                  </ToolbarButton>
                </Tooltip>
              )}
            />
            <QuickFilterControl
              render={({ ref, ...controlProps }) => (
                <QuickFilterSearchInput
                  {...controlProps}
                  ref={ref}
                  sx={{
                    gridArea: '1 / 1',
                    overflowX: 'clip',
                    width: reserveExpandedWidth
                      ? SEARCH_FIELD_WIDTH
                      : (state.expanded ? SEARCH_FIELD_WIDTH : 'var(--trigger-width, 40px)'),
                    opacity: state.expanded ? 1 : 0,
                    pointerEvents: state.expanded ? 'auto' : 'none',
                    transition: (theme) => theme.transitions.create(
                      reserveExpandedWidth ? 'opacity' : ['width', 'opacity'],
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}
      />
    </Box>
  );
}
